import { useCallback, useEffect, useRef } from "react";

import { useAddLeagueNamingKey, useAddTournamentNamingKey, usePrefetchOnMount } from "@/hooks";
import { useT } from "@/i18n";
import { prefetchPage } from "@/routes/prefetch";
import { AsyncInlineCommand, DialogTitle } from "@/ui";

import { SearchOpenTrigger } from "./GlobalSearch.analytics";
import { GlobalSearchItem } from "./GlobalSearch.types";
import { SearchItemView, useGetSearchItemTarget, useOpenSearchItemTarget } from "./SearchItemView";
import { SuperAdminModeButton } from "./SuperAdminModeButton";
import { useGlobalSearch } from "./useGlobalSearch";
import { useGlobalSearchAnalytics } from "./useGlobalSearchAnalytics";
import { useGlobalSearchMode } from "./useGlobalSearchMode";

export type InlineGlobalSearchProps = {
  className?: string;
  onAction?: (option: GlobalSearchItem) => void;
  /** Defaults to "focus" — surfaces that have a more specific trigger (e.g.
   * the mobile search button) should pass their own. */
  openTrigger?: SearchOpenTrigger;
};

export const InlineGlobalSearch = ({
  className,
  onAction,
  openTrigger = "focus",
}: InlineGlobalSearchProps) => {
  // Mobile flow opens the inline search behind a button tap — at that point
  // the user is one tap away from picking a target, so it's worth warming all
  // chunks reachable from the popover (list pages with deep-link, league /
  // license edit pages, purchased-licenses for secondary actions).
  usePrefetchOnMount(
    prefetchPage.usersList,
    prefetchPage.tournamentsList,
    prefetchPage.clubsList,
    prefetchPage.teamsList,
    prefetchPage.leagueEditLegacy,
    prefetchPage.licenseEdit,
    prefetchPage.purchasedLicense
  );

  const analytics = useGlobalSearchAnalytics();
  const lastItemsRef = useRef<GlobalSearchItem[]>([]);

  const handleQueryExecuted = useCallback(
    (props: {
      keyword: string;
      page: number;
      resultCount: number;
      items: GlobalSearchItem[];
      latencyMs: number;
    }) => {
      lastItemsRef.current = props.items;
      analytics.recordQueryExecuted(props);
    },
    [analytics]
  );

  const { mode, canUseSuperAdminMode, isSuperAdminMode, toggleSuperAdminMode } =
    useGlobalSearchMode();

  const {
    loadOptions,
    getOptionLabel,
    getOptionKey,
    resetKey,
    recentItems,
    addRecentItem,
    isShowingRecents,
  } = useGlobalSearch({ mode, onQueryExecuted: handleQueryExecuted });
  const addLeagueNamingKey = useAddLeagueNamingKey();
  const addTournamentNamingKey = useAddTournamentNamingKey();
  const t = useT();
  const getSearchItemTarget = useGetSearchItemTarget(mode);
  const openSearchItemTarget = useOpenSearchItemTarget();

  // Inline variant is mounted only while the surface (mobile tray, etc.) is
  // open, so mount/unmount is the natural session boundary.
  useEffect(() => {
    analytics.open(openTrigger);
    return () => {
      analytics.close();
    };
    // analytics + openTrigger are stable for a given mount; we intentionally
    // only run this on mount/unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = t("general.globalSearch.label.base", {
    tournaments: t(addTournamentNamingKey("general.globalSearch.label.tournaments")),
    leagues: t(addLeagueNamingKey("general.globalSearch.label.leagues")),
    venues: t("general.globalSearch.label.venues"),
  });

  return (
    <div className="flex flex-col flex-1">
      <div className="flex items-center justify-between gap-2">
        <DialogTitle title={label} />
        {canUseSuperAdminMode && (
          <SuperAdminModeButton isActive={isSuperAdminMode} onPress={toggleSuperAdminMode} />
        )}
      </div>
      <AsyncInlineCommand
        label={label}
        autoFocus
        renderOption={(option) => <SearchItemView item={option} isRecent={isShowingRecents} />}
        loadOptions={loadOptions}
        defaultOptions={recentItems}
        resetKey={resetKey}
        getOptionLabel={(option) => getOptionLabel(option)}
        getOptionValue={(option) => option.id.toString()}
        getOptionKey={getOptionKey}
        className={className}
        menuProps={{
          className: "flex-[1_1_0] overflow-y-auto",
        }}
        menuItemProps={{
          style: {
            height: "40px",
          },
          className: "h-10",
        }}
        onOptionAction={(option) => {
          if (!option) return;

          const wasRecent = recentItems.some((item) => item.id === option.id);
          const items = lastItemsRef.current.length > 0 ? lastItemsRef.current : recentItems;
          analytics.recordResultSelected({ option, items, wasRecent });

          addRecentItem(option);
          onAction?.(option);

          const target = getSearchItemTarget(option);

          if (target) {
            openSearchItemTarget(target);
          }
          analytics.close("selected");
        }}
        placeholder={label}
      />
    </div>
  );
};
